import { memo, type ComponentPropsWithoutRef } from "react";
import { defineMessages, useIntl } from "react-intl";

import { UserTermsOfService, MasterPrivacyPolicyUrl } from "common/tos";
import { useForm } from "common/core/form";
import { useA11y } from "common/accessibility";
import { TextInput } from "common/core/form/text";
import { CheckboxLabel, Checkbox } from "common/core/form/option";
import { isAriaInvalid, FormattedFieldError } from "common/core/form/error";
import { AddressInput, type Address } from "common/core/form/address";
import { useCompanyName } from "util/feature_detection";

import {
  SpacedMultipartFormRow,
  Subheader,
  Form,
  Fields,
  CheckboxContainer,
  Actions,
} from "./form";

export type NotaryColocatedInformationFormValues = {
  firstName: string;
  lastName: string;
  address: Address;
  termsAgree: "yes";
};
type Props = {
  onSubmit: (formValues: NotaryColocatedInformationFormValues) => void;
  onBack: ComponentPropsWithoutRef<typeof Actions>["onBack"];
};

const MESSAGES = defineMessages({
  formHeader: {
    id: "49c5d60f-af9e-4421-9f9b-dc24221d34ac",
    defaultMessage: "The witness is physically with you.",
  },
  enterInfo: {
    id: "677223bc-47eb-403a-b6b7-21799204df67",
    defaultMessage: "Please have witness enter information below:",
  },
  continue: {
    id: "dae93021-fc3d-4198-befa-898127425a02",
    defaultMessage: "Continue",
  },
  firstName: {
    id: "05ca31e8-3ebc-4792-a479-f6df9b403df9",
    defaultMessage: "First Name",
  },
  lastName: {
    id: "71abfb01-dd4e-4923-9858-fc866590e99e",
    defaultMessage: "Last Name",
  },
  termsAgree: {
    id: "05f8d50c-173f-4373-93dc-884594a7aad4",
    defaultMessage:
      "By checking this box, I am agreeing that I am the Witness named above and I am further agreeing to the {companyName} {terms}. For information on our privacy and data use practices please see our {privacy}.",
  },
  requiredAgreement: {
    id: "80604907-7765-4929-87d4-56df1518fb3a",
    defaultMessage: "You must agree before proceeding.",
  },
});

function CollectNotaryColocatedWitnessInformation({ onBack, onSubmit }: Props) {
  const intl = useIntl();
  const form = useForm<NotaryColocatedInformationFormValues>();
  const { useLabelledOrDescribedBy } = useA11y();
  const { errors } = form.formState;
  const companyName = useCompanyName();
  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <Fields>
        <Subheader>{intl.formatMessage(MESSAGES.formHeader)}</Subheader>

        <p>{intl.formatMessage(MESSAGES.enterInfo)}</p>

        <SpacedMultipartFormRow>
          <TextInput
            placeholder={intl.formatMessage(MESSAGES.firstName)}
            aria-invalid={isAriaInvalid(errors.firstName)}
            {...form.register("firstName", { required: true })}
          />

          <TextInput
            placeholder={intl.formatMessage(MESSAGES.lastName)}
            aria-invalid={isAriaInvalid(errors.lastName)}
            {...form.register("lastName", { required: true })}
          />
        </SpacedMultipartFormRow>

        <AddressInput<"address", NotaryColocatedInformationFormValues> form={form} name="address" />

        <CheckboxContainer>
          <div>
            <CheckboxLabel
              label={
                intl.formatMessage(MESSAGES.termsAgree, {
                  terms: <UserTermsOfService />,
                  privacy: <MasterPrivacyPolicyUrl />,
                  companyName,
                }) as string
              }
              checkbox={
                <Checkbox
                  value="yes"
                  aria-invalid={isAriaInvalid(errors.termsAgree)}
                  aria-describedby={useLabelledOrDescribedBy("termsAgree")}
                  {...form.register("termsAgree", {
                    required: intl.formatMessage(MESSAGES.requiredAgreement),
                  })}
                />
              }
            />
          </div>

          <div>
            <FormattedFieldError inputName="termsAgree" error={errors.termsAgree} />
          </div>
        </CheckboxContainer>
      </Fields>

      <Actions onBack={onBack}>{intl.formatMessage(MESSAGES.continue)}</Actions>
    </Form>
  );
}

export default memo(CollectNotaryColocatedWitnessInformation);
